import { watch } from 'vue'
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import { i18n } from '@/i18n'
import { seoEn } from '@/i18n/seo'
import { persistReferralCode, REGISTRATION_ROUTE_PATH } from '@/modules/auth/utils/referralLanding'
import { sponsorRouteRedirect } from '@/modules/profile/services/sponsorAccess'
import { getLevelById, normalizeLevelId } from '@/modules/matrix/levels'
import { rememberPortalReturn, takePortalReturn } from '@/modules/payment/services/portalSafety'
import { hasTelegramLaunchParams, readTelegramInitData } from '@/mini-app/telegramLaunchParams'

const routes: RouteRecordRaw[] = [
  {
    path: '/gnk-lab',
    name: 'gnk-lab',
    component: () => import('@/modules/auth/pages/GnkLabPage.vue'),
    meta: { title: 'nav.titles.landing', public: true, layout: 'blank' },
  },
  {
    path: '/',
    name: 'landing',
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
      layout: 'blank',
    },
  },
  {
    path: REGISTRATION_ROUTE_PATH,
    name: 'registration',
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
      layout: 'blank',
    },
  },
  {
    path: '/ai-studio',
    name: 'ai-studio',
    component: () => import('@/modules/ai-studio/pages/AiStudioLandingPage.vue'),
    meta: {
      title: 'aiStudio.title',
      public: true,
      layout: 'blank',
      productLanding: true,
    },
  },
  {
    path: '/ami',
    name: 'ami',
    component: () => import('@/modules/ami/pages/AmiLandingPage.vue'),
    meta: {
      title: 'dashboardCommand.products.items.ami.title',
      public: true,
      layout: 'blank',
    },
  },
  {
    path: '/nyro-art',
    name: 'nyro-art',
    component: () => import('@/modules/nyro-art/pages/NyroArtLandingPage.vue'),
    meta: {
      title: 'dashboardCommand.products.items.nyroArt.title',
      public: true,
      layout: 'blank',
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    redirect: (to) => ({ name: 'dashboard-prestart', query: to.query, hash: to.hash }),
    meta: {
      title: 'nav.titles.dashboard',
    },
  },
  {
    path: '/dashboard/prestart/',
    name: 'dashboard-prestart',
    component: () => import('@/modules/dashboard/pages/PrestartPage.vue'),
    meta: { title: 'nav.titles.dashboard' },
  },
  {
    path: '/dashboard/prestart-preview',
    redirect: { name: 'dashboard-prestart' },
  },
  {
    path: '/affiliate',
    redirect: '/affiliate/partner',
  },
  {
    path: '/affiliate/partner',
    name: 'affiliate-partner',
    component: () => import('@/modules/affiliate/pages/PartnerProgramPage.vue'),
    meta: {
      title: 'nav.titles.affiliatePartner',
    },
  },
  {
    path: '/affiliate/teams',
    name: 'affiliate-teams',
    component: () => import('@/modules/affiliate/pages/AffiliateTeamsPage.vue'),
    meta: {
      title: 'nav.titles.affiliateTeams',
    },
  },
  {
    path: '/affiliate/curator',
    name: 'affiliate-curator',
    component: () => import('@/modules/affiliate/pages/CuratorTab.vue'),
    meta: { title: 'nav.titles.affiliateCurator' },
  },
  {
    path: '/affiliate/info',
    redirect: '/affiliate/partner',
  },
  {
    path: '/teams',
    redirect: '/affiliate/teams',
  },
  {
    path: '/team',
    redirect: '/affiliate/teams',
  },
  {
    path: '/wallet',
    name: 'wallet',
    component: () => import('@/modules/wallet/pages/WalletDashboardPage.vue'),
    meta: {
      title: 'nav.titles.wallet',
    },
  },
  {
    path: '/wallet/history',
    name: 'wallet-history',
    component: () => import('@/modules/wallet/pages/WalletHistoryPage.vue'),
    meta: {
      title: 'nav.titles.walletHistory',
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/modules/profile/pages/ProfilePage.vue'),
    meta: {
      title: 'nav.titles.profile',
    },
  },
  {
    path: '/tma-init',
    name: 'tma-init',
    component: () => import('@/mini-app/TmaInitPage.vue'),
    meta: {
      title: 'Telegram Mini-App',
      public: true,
      tma: true,
    },
  },
  {
    path: '/r/:login',
    name: 'referral-landing',
    beforeEnter: (to, _from, next) => {
      const login = typeof to.params.login === 'string' ? to.params.login : Array.isArray(to.params.login) ? to.params.login[0] : ''
      persistReferralCode(login)
      next()
    },
    component: () => import('@/modules/auth/pages/LandingPage.vue'),
    meta: {
      title: 'nav.titles.landing',
      public: true,
      layout: 'blank',
    },
  },
  {
    path: '/payment/onchainpay',
    name: 'payment-onchainpay',
    component: () => import('@/modules/payment/pages/OnchainpayPage.vue'),
    meta: {
      title: 'onchainpay.title',
      tma: true,
    },
  },
  {
    path: '/payment/portal',
    name: 'payment-portal',
    component: () => import('@/modules/payment/pages/PortalPaymentPage.vue'),
    meta: {
      title: 'portal.title',
      tma: true,
    },
  },
  {
    path: '/chains',
    name: 'chains',
    redirect: (to) => ({ name: 'matrix-level', params: { level: 'F1' }, query: to.query, hash: to.hash }),
    meta: {
      title: 'nav.titles.chains',
    },
  },
  {
    path: '/chains/demo',
    name: 'flows-demo',
    component: () => import('@/modules/matrix/demo/MatrixDemoScreen.vue'),
    props: { initialLevel: 'F1' },
    meta: { title: 'nav.titles.chains' },
  },
  {
    path: '/chains/:level',
    name: 'matrix-level',
    component: () => import('@/modules/matrix/views/MatrixEntryScreen.vue'),
    props: (to) => ({ initialLevel: normalizeLevelId(String(to.params.level ?? '')) }),
    beforeEnter: (to, _from, next) => {
      const raw = String(to.params.level ?? '')
      const canonical = normalizeLevelId(raw)
      if (!getLevelById(canonical)) return next('/chains')
      if (canonical !== raw) return next(`/chains/${canonical}`) // легаси R4 → F4
      next()
    },
    meta: {
      title: 'nav.titles.chains',
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => '/',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Product overlays own their scroll; keep the dashboard underneath in place.
    if (to.meta.productLanding) {
      return from.name === 'dashboard' || from.name === 'dashboard-prestart' || from.meta.productLanding
        ? false
        : { left: 0, top: 0 }
    }
    if (from.meta.productLanding && (to.name === 'dashboard' || to.name === 'dashboard-prestart')) return false
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash }
    // Matrix level/UUID switches and query filters reuse the mounted page.
    if (to.name === from.name) return false
    return { left: 0, top: 0 }
  },
})

// Clean spa_auth_token from URL immediately on page load (before any navigation)
// This ensures token doesn't remain in address bar even during redirects
const telegramLaunch = hasTelegramLaunchParams() || readTelegramInitData(window.Telegram?.WebApp?.initData) !== null
let telegramBootstrap = telegramLaunch
let pendingSpaAuthToken: string | null = null

const cleanSpaAuthTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const token = urlParams.get('spa_auth_token')
  if (token) {
    // Save token for later exchange
    if (!telegramLaunch) pendingSpaAuthToken = token
    // Clean URL immediately
    urlParams.delete('spa_auth_token')
    const cleanSearch = urlParams.toString()
    const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '') + window.location.hash
    window.history.replaceState({}, '', cleanUrl)
  }
}

// Execute immediately
cleanSpaAuthTokenFromUrl()

// Global navigation guard to protect routes
router.beforeEach(async (to, from, next) => {
  if (to.name === 'payment-portal') rememberPortalReturn(to.query.attempt_id)
  const { useProfileStore } = await import('@/modules/profile/store/profileStore')
  const { useLayoutStore } = await import('@/store/layoutStore')
  const { createTokenStorage } = await import('@space/shared-api-client')

  const profileStore = useProfileStore()
  const layoutStore = useLayoutStore()
  const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
  const tokenStorage = createTokenStorage({ storageKey })

  // Initial Mini App navigation precedes main.ts auth. Neither an old browser
  // session nor an unrelated OAuth URL token may load protected account data.
  if (telegramBootstrap) {
    telegramBootstrap = false
    tokenStorage.clearToken()
    if (to.query.spa_auth_token) {
      const query = { ...to.query }
      delete query.spa_auth_token
      next({ path: to.path, query, hash: to.hash, replace: true })
      return
    }
  }

  // Handle OAuth token pickup (Google Sign-In redirect)
  // Token was saved from URL before cleaning, now exchange it
  if (pendingSpaAuthToken) {
    const tokenToExchange = pendingSpaAuthToken
    pendingSpaAuthToken = null // Clear to prevent re-exchange

    // Exchange one-time token for permanent session hash
    try {
      const response = await fetch('/api/auth/exchange-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenToExchange }),
      })

      if (response.ok) {
        const data = await response.json()
        // API returns {result: 'success', data: {success: true, token: '...'}}
        const tokenData = data.data ?? data
        if ((data.result === 'success' || tokenData.success) && tokenData.token) {
          tokenStorage.setToken(tokenData.token)
          console.log('[Router] OAuth token exchanged and stored')

          // Load user profile to complete authentication
          try {
            await profileStore.fetchProfile(true)
            console.log('[Router] Profile loaded after OAuth token exchange')
          } catch (profileError) {
            console.warn('[Router] Failed to load profile after OAuth exchange:', profileError)
          }

        } else {
          console.error('[Router] OAuth token exchange failed:', data)
        }
      } else {
        console.error('[Router] OAuth token exchange request failed:', response.status)
      }
    } catch (error) {
      console.error('[Router] OAuth token exchange error:', error)
    }

    // Роутер построил первый маршрут раньше, чем адрес вычистили
    // (cleanSpaAuthTokenFromUrl), поэтому `to.query` всё ещё несёт токен, и
    // без этого перехода Vue Router вернул бы его в адресную строку. Так и
    // получалось «/b?spa_auth_token=…» из прецедента 2026-09-23: сгоревший
    // токен жил в адресе, перезагрузка меняла его ещё раз — и снова 406.
    // Уходим на тот же адрес без токена при ЛЮБОМ исходе обмена: при удаче
    // guard ниже увидит ключ сессии и уведёт с лендинга, при неудаче страница
    // откроется гостю, а одноразовый токен второй раз никто не примет.
    const cleanQuery = { ...to.query }
    delete cleanQuery.spa_auth_token
    next({ path: to.path, query: cleanQuery, hash: to.hash, replace: true })
    return
  }


  // Start navigation loading only for actual route changes (different path),
  // not for query-only changes (e.g., filter updates on the same page).
  // Param-only changes within the same route (e.g. /chains/F1 → /chains/F2)
  // reuse the mounted component — overlay would flash the whole page.
  if (from.name && to.path !== from.path && to.name !== from.name && !to.meta.productLanding && !from.meta.productLanding) {
    layoutStore.startNavigation()
  }

  const isPublicRoute = to.meta?.public === true
  const hasToken = !!tokenStorage.getToken()

  // Resolve the sponsor before mounting a destination, including public product pages.
  if (hasToken) {
    if (!profileStore.profile && profileStore.loading) {
      await new Promise<void>(resolve => {
        const stop = watch(() => profileStore.loading, loading => {
          if (!loading) { stop(); resolve() }
        })
      })
    }
    await profileStore.fetchProfile()
    const sponsorRedirect = sponsorRouteRedirect(profileStore.profile, to.name)
    if (sponsorRedirect) {
      profileStore.sponsorPromptOpen = true
      next(sponsorRedirect)
      return
    }
  }


  // Restore only our UUID-scoped Portal route after login, never a supplied URL.
  if (hasToken && ['landing', 'registration', 'dashboard', 'dashboard-prestart', 'tma-init'].includes(String(to.name))) {
    const attemptId = takePortalReturn()
    if (attemptId) {
      next({ name: 'payment-portal', query: { attempt_id: attemptId } })
      return
    }
  }


  // If going to landing page and user has auth token, redirect to dashboard immediately
  // Don't wait for profile to load - this prevents landing page flash
  if ((to.name === 'landing' || to.name === 'registration') && hasToken) {
    next({ name: 'dashboard' })
    return
  }

  // TMA init page: if already authenticated, redirect to dashboard
  if (to.name === 'tma-init' && hasToken) {
    // --- Debug breadcrumb ---
    try {
      const { tmaAddBreadcrumb } = await import('@/mini-app/tmaDebugLogger')
      tmaAddBreadcrumb('Router: tma-init + hasToken → redirect /dashboard', {
        from: from.path,
        to: to.path,
      })
    } catch { /* ignore */ }
    next({ name: 'dashboard' })
    return
  }

  // If route is public, allow access
  if (isPublicRoute) {
    next()
    return
  }

  // If user doesn't have token, redirect to landing (or TMA flow if they're a TMA user)
  if (!hasToken) {
    // If user previously entered via Telegram Mini-App, send them back to /tma-init
    const isTmaUser = document.cookie.split('; ').some(c => c === 'tma_user=1')
    if (isTmaUser && to.name !== 'tma-init') {
      // --- Debug breadcrumb ---
      try {
        const { tmaAddBreadcrumb } = await import('@/mini-app/tmaDebugLogger')
        tmaAddBreadcrumb('Router: no token + tma_user cookie → redirect /tma-init', {
          from: from.path,
          to: to.path,
        })
      } catch { /* ignore */ }
      next({ name: 'tma-init' })
      return
    }
    next({ name: 'landing' })
    return
  }

  // For all authenticated routes: allow immediate access
  // Profile will be loaded in background by AppLayout's initializeApp()
  // This allows page skeletons to render immediately while data loads
  next()
})

function updatePageMetadata(): void {
  const name = router.currentRoute.value.name
  const page = typeof name === 'string' && Object.hasOwn(seoEn, name) ? name : 'landing'
  document.title = i18n.global.t(`seo.${page}.title`)
  const descriptions = document.head.querySelectorAll<HTMLMetaElement>('meta[name="description"]')
  const description = descriptions[0] ?? document.createElement('meta')
  description.name = 'description'
  description.content = i18n.global.t(`seo.${page}.description`)
  if (!description.parentNode) document.head.append(description)
  descriptions.forEach((meta, index) => { if (index > 0) meta.remove() })
}

watch(i18n.global.locale, updatePageMetadata)

router.afterEach(async (_to, _from, failure) => {
  if (failure) return
  updatePageMetadata()
  // End navigation loading
  const { useLayoutStore } = await import('@/store/layoutStore')
  const layoutStore = useLayoutStore()

  // Small delay to allow component to mount before hiding loader
  setTimeout(() => {
    layoutStore.endNavigation()
  }, 100)

})

// Handle dynamic import errors (e.g., after deployment when chunks have new hashes)
router.onError((error, to) => {
  const isChunkLoadError =
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS')

  if (!isChunkLoadError) return

  // In dev, a chunk failure almost always means Vite's optimized-deps cache is stale
  // (504 "Outdated Optimize Dep"). Silent reload masks the real problem and loops the
  // user back to the previous route — surface the error and instructions instead.
  if (import.meta.env.DEV) {
    console.error(
      '[Router] Dynamic import failed in dev — likely a stale Vite optimize-deps cache.\n' +
      'Fix: stop the dev server, run `rm -rf node_modules/.vite` and restart `npm run dev` (or `npm run dev -- --force`).',
      { to: to.fullPath, error },
    )
    return
  }

  // Prod: after a deploy, chunk hashes change and old HTML references missing files.
  // Reload once per path to pick up fresh assets; guard against infinite loops.
  const reloadKey = 'chunk_reload_' + to.fullPath
  const lastReload = sessionStorage.getItem(reloadKey)
  const now = Date.now()

  if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
    sessionStorage.setItem(reloadKey, now.toString())
    console.warn('[Router] Chunk load failed, reloading page to get fresh assets...', error.message)
    window.location.reload()
  } else {
    console.error('[Router] Chunk load failed even after reload', error)
  }
})

export default router
